import React, { useState, useMemo, useCallback } from 'react';
import {
  Container,
  ContentContainer,
  WidgetHeader,
  WidgetContent,
  WidgetContentInner,
} from '../../styled';

import { getUserByEmail, studentRegister } from '../../../api';
import { ActionFnParams, ActionLookup, UpComm } from '../../basic/UpComm';
import {
  OverrideData,
  ProfileCtx,
  ProfileEventTypes,
  useProfileUpComm
} from '../Profile/ProfileData';
import { UserDataKeys } from '../Profile/UserDataKeys';
import { Router } from '@reach/router';
import { CreateProfileBoundary } from '../../boundaries/CreateProfileBoundary';
import { EmailSentBoundary } from '../../boundaries/EmailSentBoundary';
import { buildPathTo, navigateTo } from '../../../navigation/path-builder';
import { APIError } from '../../../APIError';
import { ErrorContent } from '../../ErrorContent';
import { buildForBackend } from '../StudentProfile/StudentProfile';
import { DiscoverBoundary } from '../../boundaries/DiscoverBoundary';
import { GlobalStyles } from 'react-magma-dom';
import { NotFound } from '../../router-wrap/NotFound';
import { Moved } from '../../router-wrap/Moved';
import { AlfredGlobalStyles } from '../../basic/AlfredGlobalStyles';
import { ErrorBoundaryDefault } from '../../error/ErrorBoundaryDefault';
import { Main } from '../../a11y/landmark/Main';
import { Banner } from '../../a11y/landmark/Banner';
import { LandingBoundary } from '../../boundaries/LandingBoundary';
import { UCNavigateListener } from '../../upcomm/UCNavigateListener';
import { StudentSchoolSearchBoundary } from '../../boundaries/StudentSchoolSearchBoundary';
import { RedirectStep } from '../../steps/RedirectStep';
import { CreateProfileTestProps } from '../../CreateProfile';
import { CengageLogo } from '../../basic/CengageLogo';
import { duplicateLoginReason } from '../../data-inputs/validated/Validator';
import { ReturningUserFooter } from '../../basic/ReturningUserFooter';
import { trackErrorEvent } from '../../../analytics/errorTypeHelper';
import { pushErrorEventToDataLayer } from '../../../analytics/pushErrorEventToDataLayer';

export interface RegisterTestProps {
  createProfile?: CreateProfileTestProps;
}

interface RegisterProps {
  relayState: string;
  errors: string;
  email: string;
  uxmode: any;
  contextId: string

  __test?: RegisterTestProps;
}

export const StudentRegistration: React.FunctionComponent<RegisterProps> = (
  props: RegisterProps
) => {
  const overrideValues: any = {};
  const overrideHidden: any = {};
  if (props.uxmode) {
    if (props.uxmode.values) {
      overrideValues[UserDataKeys.timezone] =
        props.uxmode.values[UserDataKeys.timezone];
      overrideValues[UserDataKeys.instId] =
        props.uxmode.values[UserDataKeys.instId];
      overrideValues[UserDataKeys.instName] =
        props.uxmode.values[UserDataKeys.instName];
      overrideValues[UserDataKeys.birthYear] =
        props.uxmode.values[UserDataKeys.birthYear];
      overrideValues[UserDataKeys.firstName] =
        props.uxmode.values[UserDataKeys.firstName];
      overrideValues[UserDataKeys.lastName] =
        props.uxmode.values[UserDataKeys.lastName];
      overrideValues[UserDataKeys.skipEmailVerification] =
        props.uxmode.values[UserDataKeys.skipEmailVerification];
    }
    if (props.uxmode.hidden) {
      overrideHidden[UserDataKeys.timezone] =
        props.uxmode.hidden[UserDataKeys.timezone];
      overrideHidden[UserDataKeys.instId] =
        props.uxmode.hidden[UserDataKeys.instId];
      overrideHidden[UserDataKeys.instName] =
        props.uxmode.hidden[UserDataKeys.instName];
      overrideHidden[UserDataKeys.birthYear] =
        props.uxmode.hidden[UserDataKeys.birthYear];
      overrideHidden[UserDataKeys.firstName] =
        props.uxmode.hidden[UserDataKeys.firstName];
      overrideHidden[UserDataKeys.lastName] =
        props.uxmode.hidden[UserDataKeys.lastName];
    }
  }

  const [serverError, setServerError] = useState(false);
  const [registerErrors, setRegisterErrors] = useState({});
  const [redirectTo, setRedirectTo] = useState(undefined as string | undefined);
  const [errorMsg, setErrorMsg] = useState('');
  const [dataToSave] = useState({
    // These default to null so that we send an explicit missing when SAWS is down
    [UserDataKeys.instId]: null,
    [UserDataKeys.instName]: null
  } as any);
  const [dataRequired] = useState({} as any);
  const initCtx = {
    overrides: { hidden: overrideHidden, values: overrideValues }
  };

  const getMissingItems = useCallback((): any[] => {
    const missingData = [];
    for (const requiredKey of Object.keys(dataRequired)) {
      if (
        undefined === dataToSave[requiredKey] ||
        null === dataToSave[requiredKey]
      ) {
        missingData.push(dataRequired[requiredKey]);
      }
    }
    return missingData;
  }, [dataRequired, dataToSave]);

  const discoverUser = useCallback(
    async (userData: any) => {
      const username = userData[UserDataKeys.login];

      const result = await getUserByEmail(username);

      if (undefined === result.activating) {
        throw new Error('Expected "activating" to be defined');
      }

      if (result.activating) {
        // Tell the user that an email has been sent
        navigateTo.register.emailSent({ username });
      }

      return result;
    },
    []
  );

  const confirmValid = useCallback(
    ({ ctx }: { ctx: any }) => {
      const missingItems = getMissingItems();
      const oldIsValid = ctx.isValid;
      ctx.isValid = 0 === missingItems.length;

      if (oldIsValid !== ctx.isValid) {
        // Cause a refresh
        ctx = { ...ctx };
      }
      return ctx;
    },
    [getMissingItems]
  );
  
  const navigateUser = (registerResult: any, username: string) => {
    if (registerResult?.redirectTo) {
      if (registerResult?.redirectTo.includes('/welcome/')) {
        window.location.assign(registerResult?.redirectTo);
      } else {
        setRedirectTo(registerResult?.redirectTo);
      }
    } else {
      // Tell the user that an email has been sent
      navigateTo.register.emailSent({ username });
    }
  };

  const findOrRegisterUser = useCallback(
    async (userData: any, ctx: any) => {
      try {
        const result = await discoverUser(userData);
        if (!result.activating) {
          const username = userData[UserDataKeys.login];
          const [registerResult] =
            (await studentRegister(buildForBackend(userData)))?.results ?? [];
          navigateUser(registerResult, username);
        }
      } catch (e) {
        if (e instanceof APIError) {
          // Removing any previously stored register errors
          setRegisterErrors({});
          if (e.getErrorCode() === 'external.used-login') {
            trackStudentDuplicateLoginError(e);
            setRegisterErrors({ [UserDataKeys.login]: duplicateLoginReason, ...registerErrors });
            // Removing loginId from the dataToSave object and rerunning confirmValid so that ctx.isValid will be set to
            // false, thereby disabling the NEXT (submit) button on the create-profile page
            delete dataToSave[UserDataKeys.login];
            ctx = confirmValid({ ctx });
          } else {
            trackStudentRegistrationError(e);
            setServerError(true);
            setErrorMsg(e.message);
          }
        } else {
          trackStudentRegistrationError(e as Error);
          setServerError(true);
          // Clear the previous message and go with the default message
          setErrorMsg('');
        }
      }
      return ctx;
    },
    [confirmValid, dataToSave, discoverUser, registerErrors]
  );

  const actionLookup: ActionLookup<ProfileCtx> = useMemo(
    () => ({
      [ProfileEventTypes.dataInit]: initializeData(dataToSave),
      [ProfileEventTypes.dataChanged]: onDataChanged(dataToSave, confirmValid),
      [ProfileEventTypes.dataInvalid]: onDataInvalid(dataToSave, confirmValid),
      [ProfileEventTypes.dataSaved]: onDataSaved(getMissingItems, findOrRegisterUser, dataToSave),
      [ProfileEventTypes.dataRequired]: onDataRequired(dataRequired, confirmValid),
      [ProfileEventTypes.discoverUsername]: onDiscoverUsername(discoverUser, dataToSave),
      [ProfileEventTypes.pageUnload]: onPageUnload(dataToSave, dataRequired)
    }),
    [
      confirmValid,
      dataRequired,
      dataToSave,
      discoverUser,
      findOrRegisterUser,
      getMissingItems
    ]
  );

  return (
    <Container id="okta-login-container">
      <GlobalStyles />
      <AlfredGlobalStyles />
      <ContentContainer>
        <Banner>
          <WidgetHeader>
            <CengageLogo />
          </WidgetHeader>
        </Banner>

        <Main>
          <WidgetContent>
            <WidgetContentInner>
              <ErrorBoundaryDefault>
                <UpComm actionLookup={actionLookup} initCtx={initCtx}>
                  {serverError && <ErrorContent msg={errorMsg} />}

                  {redirectTo && <RedirectStep redirectTo={redirectTo} />}

                  {!serverError && !redirectTo && (
                    <UCNavigateListener useUpComm={useProfileUpComm}>
                      <Router basepath={buildPathTo.onlyPath.register.root()}>
                        <NotFound
                          basePath={buildPathTo.register.root()}
                          label={'the start of registration'}
                        />
                        <Moved
                          path="/"
                          movedTo={buildPathTo.register.landing({})}
                        />
                        <DiscoverBoundary />
                        <CreateProfileBoundary
                          __test={props.__test?.createProfile}
                          registerErrors={registerErrors}
                        />
                        <EmailSentBoundary />
                        <LandingBoundary />
                        <StudentSchoolSearchBoundary />
                      </Router>
                    </UCNavigateListener>
                  )}
                </UpComm>
              </ErrorBoundaryDefault>
            </WidgetContentInner>
            <ReturningUserFooter skipRender={serverError || window.location.pathname.indexOf('email-sent') > -1} />
          </WidgetContent>
        </Main>
      </ContentContainer>
    </Container>
  );
};

function trackStudentRegistrationError(error: Error) {
  trackErrorEvent(error, {
    error_type: 'student_registration_failed',
    error_description:
      'Student registration failed.',
    error_location: 'StudentRegistration.findOrRegisterUser',
  });
}

function trackStudentDuplicateLoginError(error: Error) {
  trackErrorEvent(error, {
    error_type: 'student_registration_duplicate_login',
    error_description:
      'Student registration duplicate loginId: account already exists for submitted username.',
    error_location: 'StudentRegistration.findOrRegisterUser'
  });
}

function onPageUnload(dataToSave: any, dataRequired: any): ({ ctx }: ActionFnParams<ProfileCtx>) => Promise<{ isValid: false; isEditing: boolean; overrides?: OverrideData | undefined; }> {
  return async ({ ctx }) => {
    // When we unload, we want to remove existing data
    // from our state
    const targets = [dataToSave, dataRequired];
    for (const target of targets) {
      for (const key of Object.keys(target)) {
        delete target[key];
      }
    }
    // Then reset the context to not be valid
    return { ...ctx, isValid: false };
  };
}

function onDiscoverUsername(discoverUser: (userData: any) => Promise<any>, dataToSave: any): (args: ActionFnParams<ProfileCtx>) => Promise<ProfileCtx> {
  return async (args) => {
    const { ctx, data = {} } = args;
    const { whenNotActivating } = data;
    const result = await discoverUser(dataToSave);
    if (whenNotActivating) {
      if (!result.activating) {
        whenNotActivating?.(dataToSave);
      }
      return { ...ctx, isValid: false } as ProfileCtx;
    }
    return ctx;
  };
}

function onDataRequired(dataRequired: any, confirmValid: ({ ctx }: { ctx: any; }) => any): ({ ctx, data }: ActionFnParams<ProfileCtx>) => ProfileCtx {
  return ({ ctx, data }) => {
    const { key, value, label } = data;
    if (value) {
      dataRequired[key] = {
        label
      };
    } else {
      delete dataRequired[key];
    }

    ctx = confirmValid({ ctx });

    return ctx;
  };
}

function onDataSaved(getMissingItems: () => any[], findOrRegisterUser: (userData: any, ctx: any) => Promise<ProfileCtx>, dataToSave: any): ({ ctx }: ActionFnParams<ProfileCtx>) => Promise<ProfileCtx> {
  return async ({ ctx }) => {
    const missingLabels = getMissingItems().map(v => v.label);
    if (0 !== missingLabels.length) {
      throw new Error(
        'Cannot continue, required data was not found: ' +
        missingLabels.join(', ')
      );
    } else {
      // Try and register/find the user
      ctx = await findOrRegisterUser(dataToSave, ctx);
    }

    return ctx;
  };
}

function onDataInvalid(dataToSave: any, confirmValid: ({ ctx }: { ctx: any; }) => any): ({ ctx, data }: ActionFnParams<ProfileCtx>) => ProfileCtx {
  return ({ ctx, data }) => {
    const { key } = data;
    delete dataToSave[key];

    ctx = confirmValid({ ctx });

    return ctx;
  };
}

function onDataChanged(dataToSave: any, confirmValid: ({ ctx }: { ctx: any; }) => any): ({ ctx, data }: ActionFnParams<ProfileCtx>) => ProfileCtx {
  return ({ ctx, data }) => {
    if (data.value) {
      dataToSave[data.key] = data.value;
    } else {
      delete dataToSave[data.key];
    }

    ctx = confirmValid({ ctx });

    return ctx;
  };
}

function initializeData(dataToSave: any): ({ ctx, data }: ActionFnParams<ProfileCtx>) => ProfileCtx {
  return ({ ctx, data }) => {
    dataToSave[data.key] = data.value;
    return ctx;
  };
}
